import React, { useState } from "react";
import {
  Layout,
  TextField,
  Button,
  Banner,
  Card,
  TextStyle,
} from "@shopify/polaris";
import get from "lodash/get";
import Toggle from "react-toggle";
import { Box } from "rebass";
import { testSembleConnection } from "services/wpApi";

export default function SembleIntegration({
  values,
  setFieldValue,
  errors,
  shop,
}) {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);

  const handleTestConnection = async () => {
    const apiKey = get(values, "semble.apiKey");

    if (!apiKey) {
      setConnectionStatus({
        success: false,
        message: "Please enter an API Key before testing connection",
      });
      return;
    }

    setTestingConnection(true);
    setConnectionStatus(null);

    try {
      const response = await testSembleConnection({ apiKey });
      setConnectionStatus({
        success: response.data.success,
        message: response.data.message,
      });
    } catch (error) {
      setConnectionStatus({
        success: false,
        message:
          error.response?.data?.message ||
          "Connection test failed. Please try again.",
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const isEnabled = get(values, "semble.enabled", false);

  return (
    <Layout.AnnotatedSection
      title="Semble Patient Label Syncing"
      description={
        <>
          <p>
            Automatically sync ID check status to patient records in
            Semble.
          </p>
        </>
      }
    >
      <Card
        fullWidth
        title={
          <div className="ri-flex ri-items-center ri-space-x-3">
            <Toggle
              checked={isEnabled}
              onChange={(event) =>
                setFieldValue("semble.enabled", event.target.checked)
              }
            />
            <span>Enable Semble Integration</span>
          </div>
        }
      >
        <Card.Section>
          {isEnabled && (
            <div className="ri-space-y-6">
              {/* API Credentials Section */}
              <div>
                <h4 className="ri-text-base ri-font-medium ri-text-gray-900 ri-mb-4">
                  API Configuration
                </h4>
                <p className="ri-text-sm ri-text-gray-600 ri-mb-4">
                  Enter your Semble API key to enable patient label syncing.
                  You can find this in your Semble account settings under API
                  access.
                </p>

                <div className="ri-space-y-4">
                  <TextField
                    label="API Key"
                    value={get(values, "semble.apiKey", "")}
                    onChange={(value) =>
                      setFieldValue("semble.apiKey", value)
                    }
                    type="password"
                    helpText="Your Semble API key for authentication"
                    error={get(errors, "semble.apiKey")}
                  />

                  <div className="ri-flex ri-items-center ri-space-x-4">
                    <Button
                      onClick={handleTestConnection}
                      loading={testingConnection}
                      disabled={!get(values, "semble.apiKey")}
                    >
                      Test Connection
                    </Button>

                    {connectionStatus && (
                      <div className="ri-flex-1">
                        <Banner
                          status={
                            connectionStatus.success ? "success" : "critical"
                          }
                        >
                          {connectionStatus.message}
                        </Banner>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* How It Works Section */}
              <div>
                <h4 className="ri-text-base ri-font-medium ri-text-gray-900 ri-mb-4">
                  How It Works
                </h4>
                <p className="ri-text-sm ri-text-gray-600 ri-mb-4">
                  The following verification statuses are automatically synced
                  as patient labels:
                </p>
                <div className="ri-space-y-2 ri-text-sm ri-text-gray-600">
                  <p>
                    • <strong>ID check in progress:</strong> Customer
                    has started but not completed verification
                  </p>
                  <p>
                    • <strong>ID check in review:</strong> Verification
                    is being manually reviewed
                  </p>
                  <p>
                    • <strong>ID check approved:</strong> Customer
                    successfully verified their identity
                  </p>
                  <p>
                    • <strong>ID check failed:</strong> Verification was
                    unsuccessful
                  </p>
                  <p>
                    • <strong>ID check manually approved:</strong>{" "}
                    Merchant manually approved the verification
                  </p>
                  <p>
                    • <strong>ID check manually rejected:</strong>{" "}
                    Merchant manually rejected the verification
                  </p>
                </div>

                <Banner status="info" icon={false}>
                  <p className="ri-text-sm">
                    <strong>Automatic Setup:</strong> Labels are created
                    automatically when needed. Changes sync in real-time as
                    verification status updates.
                  </p>
                </Banner>
              </div>
            </div>
          )}

          {!isEnabled && (
            <TextStyle variation="subdued">
              Enable Semble integration to automatically sync ID check
              status with patient records in your Semble account.
            </TextStyle>
          )}
        </Card.Section>
      </Card>
    </Layout.AnnotatedSection>
  );
}